import {
  DEFAULT_TITLE_ALIGN,
  getTitleName,
  LABEL_KEYS,
  VENN_TITLE_ALIGN,
  VENN_TITLE_NAME
} from './config';
import { VennDataItem, VennSeriesOption } from './type';
import { excludeSubArray, getCombinations } from './util';

export interface Datum {
  name: string;
  [k: string]: unknown;
}

function getCombToDatumsMap(seriesOption: VennSeriesOption) {
  const rawData = seriesOption.data;
  const dims = seriesOption.dvDimensionCount;
  const nameProperty = seriesOption.dvNameProperty || 'name';
  /** 数据的name和单条数据的映射 */
  const nameToDatum = new Map<string, Datum>();
  /** 单条数据和数据所属地图区块名的映射 */
  const nameToComb = new Map<string, string>();
  /** 地图数据区块名和区块下数据的映射 */
  const combToDatums = new Map<string, Datum[]>();
  /** 集合名和集合下数据的映射 */
  const collectionToDatums = new Map<string, Datum[]>();

  for (let i = 0; i < dims; i += 1) {
    const dimLabel = LABEL_KEYS[i];
    const list = rawData[i].children;
    list.forEach(datum => {
      const datumName = datum[nameProperty] as string;
      // 遍历所有集合的单条数据，更新nameToDatum
      if (!nameToDatum.has(datumName)) {
        nameToDatum.set(datumName, datum);
      }

      // 遍历所有集合的单条数据，更新datumToComb
      if (!nameToComb.has(datumName)) {
        nameToComb.set(datumName, dimLabel);
      } else {
        nameToComb.set(datumName, nameToComb.get(datumName) + dimLabel);
      }

      // 遍历所有集合的单条数据，更新collectionToDatums
      if (!collectionToDatums.has(dimLabel)) {
        collectionToDatums.set(dimLabel, [datum]);
      } else {
        collectionToDatums.get(dimLabel).push(datum);
      }
    });
  }

  // 根据nameToComb更新combToDatums映射
  Array.from(nameToComb.entries()).forEach(([name, comb]) => {
    const currDatum = nameToDatum.get(name);
    if (!combToDatums.has(comb)) {
      combToDatums.set(comb, [currDatum]);
    } else {
      combToDatums.get(comb).push(currDatum);
    }
  });

  return {
    nameToComb,
    nameToDatum,
    combToDatums,
    collectionToDatums
  };
}

function getTitles(seriesData: VennDataItem[], length: number) {
  return seriesData.slice(0, length).map(data => data.title);
}

export function parseRawData(seriesOption: VennSeriesOption) {
  const rawData = seriesOption.data.slice();
  const titles = getTitles(rawData, seriesOption.dvDimensionCount);
  const combinations = getCombinations(seriesOption.dvDimensionCount);
  const { combToDatums, collectionToDatums } = getCombToDatumsMap(seriesOption);
  const combinationData = combinations.map(comb => {
    const combNameGroup = comb.map(idx => LABEL_KEYS[idx]);
    const excludeCombGroup = excludeSubArray(combNameGroup, LABEL_KEYS.slice(0, titles.length));
    const combName = combNameGroup.join('');
    const datums = combToDatums.has(combName) ? combToDatums.get(combName) : [];
    return {
      name: combName,
      __type: 'region',
      __group: combNameGroup,
      __excludeGroup: excludeCombGroup,
      datums,
      value: datums.length
    };
  });
  const titleData = titles.map((title, titleIdx) => {
    const collectionName = LABEL_KEYS[titleIdx];
    const datums = collectionToDatums.get(collectionName) || [];
    const align =
      VENN_TITLE_ALIGN?.[seriesOption.map]?.[VENN_TITLE_NAME[titleIdx]] || DEFAULT_TITLE_ALIGN;
    return {
      name: getTitleName(titleIdx),
      __type: 'title',
      __group: [LABEL_KEYS[titleIdx]],
      __excludeGroup: LABEL_KEYS.filter(
        label => label !== LABEL_KEYS.slice(0, titles.length)[titleIdx]
      ),
      __titleLabel: title,
      __align: align,
      datums,
      // 标题本身不包含数据
      value: datums.length
    };
  });
  seriesOption.data = [...combinationData, ...titleData] as VennSeriesOption['data'];
  seriesOption.__titles = titles;
  seriesOption.__rawData = rawData;
  seriesOption.__combToDatums = combToDatums;
  seriesOption.__collectionToDatums = collectionToDatums;
}
